/**
 * Shipping Rates Management Page
 * Manage shipping rate configurations
 */

import { createFileRoute } from "@tanstack/react-router";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { PermissionGuard } from "@/components/auth/permission-guard";
import { PERMISSIONS } from "@/constants/permissions.constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useCurrencyFormat } from "@/hooks/use-currency-format";
import {
  useShippingRates,
  useCreateShippingRate,
  useUpdateShippingRate,
  useDeleteShippingRate,
} from "@/features/shipping-rates/hooks/use-shipping-rates";
import type { ShippingRate, CreateShippingRate } from "@/features/shipping-rates/types/shipping-rates.types";

export const Route = createFileRoute("/(app)/dashboard/shipping-rates/")({
  component: ShippingRatesPage,
});

const shippingRateSchema = z.object({
  name: z.string().min(1, "Name ist erforderlich"),
  price: z.string().regex(/^\d+(\.\d{1,2})?$/, "Ungültiger Preis"),
  description: z.string().optional(),
});

type ShippingRateForm = z.infer<typeof shippingRateSchema>;

function ShippingRatesPage() {
  const { toast } = useToast();
  useDocumentTitle("Versandkosten");
  const { formatCurrency } = useCurrencyFormat();
  const { data: shippingRates, isLoading } = useShippingRates();
  const createShippingRate = useCreateShippingRate();
  const deleteShippingRate = useDeleteShippingRate();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRate, setEditingRate] = useState<ShippingRate | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rateToDelete, setRateToDelete] = useState<string | null>(null);

  const updateShippingRate = useUpdateShippingRate(editingRate?.id || "");

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ShippingRateForm>({
    resolver: zodResolver(shippingRateSchema),
  });

  const openCreateDialog = () => {
    reset({
      name: "",
      price: "",
      description: "",
    });
    setEditingRate(null);
    setDialogOpen(true);
  };

  const openEditDialog = (rate: ShippingRate) => {
    reset({
      name: rate.name,
      price: rate.price,
      description: rate.description || "",
    });
    setEditingRate(rate);
    setDialogOpen(true);
  };

  const onSubmit = async (data: ShippingRateForm) => {
    try {
      if (editingRate) {
        await updateShippingRate.mutateAsync(data);
        toast({
          title: "Erfolgreich aktualisiert",
          description: "Der Versandtarif wurde aktualisiert.",
        });
      } else {
        await createShippingRate.mutateAsync(data as CreateShippingRate);
        toast({
          title: "Erfolgreich erstellt",
          description: "Der Versandtarif wurde erstellt.",
        });
      }
      setDialogOpen(false);
      reset();
    } catch {
      toast({
        title: "Fehler",
        description: "Der Versandtarif konnte nicht gespeichert werden.",
        variant: "destructive",
      });
    }
  };

  const confirmDelete = async () => {
    if (!rateToDelete) return;

    try {
      await deleteShippingRate.mutateAsync(rateToDelete);
      toast({
        title: "Erfolgreich gelöscht",
        description: "Der Versandtarif wurde gelöscht.",
      });
      setDeleteDialogOpen(false);
      setRateToDelete(null);
    } catch {
      toast({
        title: "Fehler",
        description: "Der Versandtarif konnte nicht gelöscht werden.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return <div className="p-6">Laden...</div>;
  }

  return (
    <div className="w-full max-w-full space-y-4 md:space-y-6 overflow-hidden">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold md:text-3xl">Versandkosten</h1>
          <p className="text-sm text-muted-foreground md:text-base">
            Verwalten Sie Ihre Versandtarife
          </p>
        </div>
        <PermissionGuard permission={PERMISSIONS.SHIPPING_CREATE}>
          <Button onClick={openCreateDialog} className="w-full sm:w-auto">
            <Plus className="h-4 w-4 mr-2" />
            Neuer Versandtarif
          </Button>
        </PermissionGuard>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {shippingRates?.map((rate: ShippingRate) => (
          <Card key={rate.id}>
            <CardHeader>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-base sm:text-lg">{rate.name}</CardTitle>
                  <CardDescription className="mt-2">
                    <span className="text-xl font-semibold text-foreground sm:text-2xl">
                      {formatCurrency(rate.price)}
                    </span>
                  </CardDescription>
                </div>
                <div className="flex gap-1 sm:gap-2 shrink-0">
                  <PermissionGuard permission={PERMISSIONS.SHIPPING_EDIT}>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditDialog(rate)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </PermissionGuard>
                  <PermissionGuard permission={PERMISSIONS.SHIPPING_DELETE}>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setRateToDelete(rate.id);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </PermissionGuard>
                </div>
              </div>
            </CardHeader>
            {rate.description && (
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  {rate.description}
                </p>
              </CardContent>
            )}
          </Card>
        ))}
      </div>

      {(!shippingRates || shippingRates.length === 0) && (
        <Card>
          <CardContent className="pt-6 text-center text-muted-foreground">
            <p>Noch keine Versandtarife vorhanden.</p>
            <p className="text-sm mt-2">
              Erstellen Sie Ihren ersten Versandtarif, um loszulegen.
            </p>
          </CardContent>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingRate ? "Versandtarif bearbeiten" : "Neuer Versandtarif"}
            </DialogTitle>
            <DialogDescription>
              {editingRate
                ? "Bearbeiten Sie die Details des Versandtarifs."
                : "Erstellen Sie einen neuen Versandtarif."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                {...register("name")}
                placeholder="z.B. Standard, Express, International"
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="price">Preis (€) *</Label>
              <Input
                id="price"
                {...register("price")}
                placeholder="0.00"
                type="text"
              />
              {errors.price && (
                <p className="text-sm text-destructive">{errors.price.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Beschreibung</Label>
              <Textarea
                id="description"
                {...register("description")}
                placeholder="Optionale Beschreibung..."
                rows={3}
              />
              {errors.description && (
                <p className="text-sm text-destructive">
                  {errors.description.message}
                </p>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Abbrechen
              </Button>
              <Button
                type="submit"
                disabled={createShippingRate.isPending || updateShippingRate.isPending}
              >
                {editingRate ? "Aktualisieren" : "Erstellen"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Versandtarif löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Diese Aktion kann nicht rückgängig gemacht werden. Der Versandtarif
              wird dauerhaft gelöscht.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setRateToDelete(null)}>
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
